
import React, { useEffect, useState } from 'react';
import { useRoughBodyStore } from '../stores/useRoughBodyStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useNavigate } from 'react-router-dom';
import { BottomDock } from '../components/Layout/BottomDock';
import { ChevronRight, Clock, CheckCircle, Search, X, Bell, Settings, Circle } from 'lucide-react';
import { StatusBadge } from '../components/UI/StatusBadge';
import { clsx } from 'clsx';
import { usePieceStore } from '../stores/usePieceStore';

export const RoughBodyList: React.FC = () => {
    const navigate = useNavigate();
    const { roughBodies, loading, fetchRoughBodies } = useRoughBodyStore();
    const { userProfile } = useAuthStore();
    const { recentLogs, fetchRecentLogs } = usePieceStore();

    const [searchTerm, setSearchTerm] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);

    useEffect(() => {
        fetchRoughBodies();
        fetchRecentLogs();
    }, []);

    // Derived Notifications logic (simplified version of dashboard)
    const notifications = React.useMemo(() => {
        return recentLogs.map(log => ({
            id: log.id,
            msg: `Sample Update`,
            sub: `${log.station} — ${log.new_status}`,
            time: new Date(log.scanned_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        })).slice(0, 10);
    }, [recentLogs]);

    const grouped = React.useMemo(() => {
        return roughBodies.filter(rb => {
            if (!showCompleted && rb.status === 'Completed') return false;

            if (searchTerm) {
                const lower = searchTerm.toLowerCase();
                const matchesCode = rb.styles?.style_code?.toLowerCase().includes(lower);
                const matchesQR = rb.qr_code?.toLowerCase().includes(lower);
                const matchesStatus = rb.status.toLowerCase().includes(lower);
                if (!matchesCode && !matchesQR && !matchesStatus) return false;
            }
            return true;
        }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [roughBodies, searchTerm, showCompleted]);

    const toggleSearch = () => {
        if (searchOpen) setSearchTerm('');
        setSearchOpen(!searchOpen);
    };

    return (
        <div className="min-h-screen bg-stone-50 flex flex-col relative font-sans text-slate-900 pb-safe">
            {/* ── Header ── */}
            <div className="bg-white/80 backdrop-blur-2xl pt-safe sticky top-0 z-40 px-5 pb-3 pt-4 border-b border-slate-100/80">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shadow-sm shadow-blue-600/20">
                            <span className="text-xs font-bold text-white uppercase">{userProfile?.full_name?.charAt(0) || 'U'}</span>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none">{userProfile?.role?.replace('_', ' ')}</p>
                            <p className="text-slate-800 font-semibold text-sm leading-tight">{userProfile?.full_name}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={toggleSearch}
                            className={clsx(
                                "w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200",
                                searchOpen ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-400 border border-slate-100"
                            )}
                        >
                            {searchOpen ? <X size={16} /> : <Search size={16} />}
                        </button>

                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className={clsx(
                                "w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 relative",
                                showNotifications ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-400 border border-slate-100"
                            )}
                        >
                            <Bell size={16} />
                            {notifications.length > 0 && (
                                <span className="absolute top-2 right-2.5 w-1.5 h-1.5 bg-red-500 rounded-full border border-white"></span>
                            )}
                        </button>

                        <button
                            onClick={() => navigate('/settings')}
                            className={clsx(
                                "w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 bg-white text-slate-400 border border-slate-100"
                            )}
                        >
                            <Settings size={16} />
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                {searchOpen && (
                    <div className="mt-3 animate-slide-down">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                            <input
                                type="text" autoFocus
                                placeholder="Search style code, QR..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 pl-9 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 transition-all font-medium"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* ── Notifications Panel ── */}
            {showNotifications && (
                <div className="fixed inset-0 z-[60] bg-black/20 backdrop-blur-sm" onClick={() => setShowNotifications(false)}>
                    <div className="absolute top-20 right-5 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-slide-down" onClick={e => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Recent Activity</h3>
                            <button onClick={() => setShowNotifications(false)}><X size={14} className="text-slate-400" /></button>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                            {notifications.length === 0 ? (
                                <div className="p-6 text-center text-slate-400 text-xs">No recent updates</div>
                            ) : (
                                notifications.map(n => (
                                    <div key={n.id} className="px-4 py-3 border-b border-slate-50 hover:bg-blue-50 transition-colors cursor-pointer">
                                        <p className="text-xs font-bold text-slate-700 mb-0.5">{n.msg}</p>
                                        <p className="text-[10px] text-slate-500 mb-0.5">{n.sub}</p>
                                        <p className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={10} /> {n.time}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* CONTENT */}
            <div className="flex-1 px-5 pb-32 animate-page pt-4">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-sm font-bold text-slate-800">Rough Bodies ({grouped.length})</h2>
                    <button
                        onClick={() => setShowCompleted(!showCompleted)}
                        className={clsx(
                            "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase transition-all",
                            showCompleted ? "bg-slate-800 text-white" : "bg-white text-slate-500 border border-slate-200"
                        )}
                    >
                        {showCompleted ? <CheckCircle size={12} /> : <Circle size={12} />}
                        Show Done
                    </button>
                </div>

                {loading && roughBodies.length === 0 ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map(i => <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />)}
                    </div>
                ) : grouped.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                        <div className="w-16 h-16 bg-blue-50 text-blue-600 flex items-center justify-center rounded-2xl mx-auto mb-4">
                            <Search size={28} />
                        </div>
                        <p className="text-sm font-bold">No Rough Bodies Found</p>
                        <p className="text-xs">Requests will appear here</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {grouped.map(rb => (
                            <div key={rb.id} onClick={() => navigate(`/rough-body/${rb.qr_code}`)} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm active:scale-[0.98] transition-all">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-sm">{rb.styles?.style_code || 'Unknown Style'}</h3>
                                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">{rb.qr_code}</p>
                                    </div>
                                    <div className={clsx(
                                        "px-2 py-0.5 rounded text-[10px] font-bold uppercase",
                                        rb.status === 'Completed' ? "bg-emerald-50 text-emerald-600" :
                                            rb.status === 'Requested' ? "bg-slate-100 text-slate-500" :
                                                "bg-blue-50 text-blue-600"
                                    )}>
                                        {rb.status}
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 text-[10px] text-slate-500 mt-3 border-t border-slate-50 pt-3">
                                    <div className="flex items-center gap-1">
                                        <span className="font-bold bg-slate-100 px-1.5 rounded text-slate-600">Size: {rb.size || '-'}</span>
                                    </div>
                                    <div className="flex items-center gap-1 ml-auto">
                                        <Clock size={10} />
                                        <span>{new Date(rb.updated_at).toLocaleDateString()}</span>
                                    </div>
                                    <ChevronRight size={14} className="text-slate-300" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <BottomDock />
        </div>
    );
};
